/** Session cache for pre-create instrument flow (picker + info) without URL query params. */
const STORAGE_KEY = 'bss:instrument-create-flow'

export type InstrumentCreateOrigin = 'campaign' | 'quick'

export type InstrumentCreateSession = {
  /** BFP-999 — `quick` skips Campaign/LOI; default/omitted is campaign-linked. */
  origin?: InstrumentCreateOrigin
  campaignId: string
  loiId: string
  /** Set when user chose a system template from the library (create flow). */
  templateId?: string
  /** Set when user chose a Built-in Instrument snapshot (BFP-606). */
  builtInInstrumentId?: string
  templateName?: string
  templateEstCompletion?: number
  /** Highlights the last-selected tile when returning from info Cancel. */
  selectedTemplateId?: string
}

export function isQuickInstrumentCreateSession(
  session: InstrumentCreateSession | null | undefined,
): boolean {
  return session?.origin === 'quick'
}

function applySourceMeta(
  payload: InstrumentCreateSession,
  context: InstrumentCreateSession,
): void {
  const name = context.templateName?.trim()
  if (name) payload.templateName = name
  if (
    typeof context.templateEstCompletion === 'number' &&
    context.templateEstCompletion >= 0
  ) {
    payload.templateEstCompletion = context.templateEstCompletion
  }
}

function applyTemplateFields(
  payload: InstrumentCreateSession,
  context: InstrumentCreateSession,
): void {
  const builtInInstrumentId = context.builtInInstrumentId?.trim()
  const templateId = context.templateId?.trim()

  if (builtInInstrumentId) {
    payload.builtInInstrumentId = builtInInstrumentId
    applySourceMeta(payload, context)
  } else if (templateId) {
    payload.templateId = templateId
    applySourceMeta(payload, context)
  }

  if (context.selectedTemplateId?.trim()) {
    payload.selectedTemplateId = context.selectedTemplateId.trim()
  }
}

export function rememberInstrumentCreateContext(
  context: InstrumentCreateSession,
): void {
  const origin: InstrumentCreateOrigin =
    context.origin === 'quick' ? 'quick' : 'campaign'

  if (origin === 'quick') {
    const payload: InstrumentCreateSession = {
      origin: 'quick',
      campaignId: '',
      loiId: '',
    }
    applyTemplateFields(payload, context)
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    } catch {
      /* quota / private mode */
    }
    return
  }

  const campaignId = context.campaignId.trim()
  const loiId = context.loiId.trim()
  if (!campaignId || !loiId) return
  const payload: InstrumentCreateSession = { origin: 'campaign', campaignId, loiId }
  applyTemplateFields(payload, context)
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    /* quota / private mode */
  }
}

/** Clears template / built-in choice while keeping campaign / LOI / tile highlight / origin. */
export function clearInstrumentTemplateSelection(): void {
  const ctx = readInstrumentCreateContext()
  if (!ctx) return
  const payload: InstrumentCreateSession = {
    origin: ctx.origin === 'quick' ? 'quick' : 'campaign',
    campaignId: ctx.campaignId,
    loiId: ctx.loiId,
  }
  if (ctx.selectedTemplateId?.trim()) {
    payload.selectedTemplateId = ctx.selectedTemplateId.trim()
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    /* quota / private mode */
  }
}

function applyParsedTemplateFields(
  session: InstrumentCreateSession,
  parsed: InstrumentCreateSession,
): void {
  const builtInInstrumentId = parsed.builtInInstrumentId?.trim()
  const templateId = parsed.templateId?.trim()
  if (builtInInstrumentId) {
    session.builtInInstrumentId = builtInInstrumentId
  } else if (templateId) {
    session.templateId = templateId
  }
  if (session.templateId || session.builtInInstrumentId) {
    const templateName = parsed.templateName?.trim()
    if (templateName) session.templateName = templateName
    if (
      typeof parsed.templateEstCompletion === 'number' &&
      parsed.templateEstCompletion >= 0
    ) {
      session.templateEstCompletion = parsed.templateEstCompletion
    }
  }
  const selectedTemplateId = parsed.selectedTemplateId?.trim()
  if (selectedTemplateId) {
    session.selectedTemplateId = selectedTemplateId
  }
}

function parseQuickCreateSession(
  parsed: InstrumentCreateSession,
): InstrumentCreateSession {
  const session: InstrumentCreateSession = {
    origin: 'quick',
    campaignId: '',
    loiId: '',
  }
  applyParsedTemplateFields(session, parsed)
  return session
}

function parseCampaignCreateSession(
  parsed: InstrumentCreateSession,
): InstrumentCreateSession | null {
  const campaignId = parsed?.campaignId?.trim()
  const loiId = parsed?.loiId?.trim()
  if (!campaignId || !loiId) return null
  const session: InstrumentCreateSession = {
    origin: 'campaign',
    campaignId,
    loiId,
  }
  applyParsedTemplateFields(session, parsed)
  return session
}

export function readInstrumentCreateContext(): InstrumentCreateSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as InstrumentCreateSession
    if (parsed?.origin === 'quick') {
      return parseQuickCreateSession(parsed)
    }
    return parseCampaignCreateSession(parsed)
  } catch {
    return null
  }
}

export function hasInstrumentCreateContext(): boolean {
  return readInstrumentCreateContext() != null
}

/** Template id for POST create — session, then router state, then `?templateId=`. */
export function resolveCreateTemplateId(
  session: InstrumentCreateSession | null,
  templateIdFromUrl?: string | null,
  templateIdFromRouter?: string | null,
): string | undefined {
  const fromSession = session?.templateId?.trim()
  if (fromSession) return fromSession
  const fromRouter = templateIdFromRouter?.trim()
  if (fromRouter) return fromRouter
  const fromUrl = templateIdFromUrl?.trim()
  if (fromUrl) return fromUrl
  const name = session?.templateName?.trim()
  const selected = session?.selectedTemplateId?.trim()
  if (name && selected && !session?.builtInInstrumentId) return selected
  return undefined
}

/** Built-in id for POST create — session, then router state, then `?builtInInstrumentId=`. */
export function resolveCreateBuiltInInstrumentId(
  session: InstrumentCreateSession | null,
  builtInIdFromUrl?: string | null,
  builtInIdFromRouter?: string | null,
): string | undefined {
  const fromSession = session?.builtInInstrumentId?.trim()
  if (fromSession) return fromSession
  const fromRouter = builtInIdFromRouter?.trim()
  if (fromRouter) return fromRouter
  const fromUrl = builtInIdFromUrl?.trim()
  if (fromUrl) return fromUrl
  return undefined
}

export function resolveCreateTemplateName(
  session: InstrumentCreateSession | null,
  options?: {
    routerTemplateName?: string | null
    fetchedTemplateName?: string | null
  },
): string | undefined {
  const fromSession = session?.templateName?.trim()
  if (fromSession) return fromSession
  const fromRouter = options?.routerTemplateName?.trim()
  if (fromRouter) return fromRouter
  const fetched = options?.fetchedTemplateName?.trim()
  return fetched || undefined
}

/** Persist template choice from router / URL into session so submit + refresh stay consistent. */
export function syncCreateTemplateIntoSession(input: {
  templateId: string
  templateName?: string
  templateEstCompletion?: number
}): InstrumentCreateSession | null {
  const ctx = readInstrumentCreateContext()
  if (!ctx) return null
  const templateId = input.templateId.trim()
  if (!templateId) return ctx
  const templateName =
    input.templateName?.trim() || ctx.templateName?.trim() || undefined
  const next: InstrumentCreateSession = {
    origin: ctx.origin === 'quick' ? 'quick' : 'campaign',
    campaignId: ctx.campaignId,
    loiId: ctx.loiId,
    templateId,
    ...(ctx.selectedTemplateId ? { selectedTemplateId: ctx.selectedTemplateId } : {}),
  }
  if (templateName) next.templateName = templateName
  if (
    typeof input.templateEstCompletion === 'number' &&
    input.templateEstCompletion >= 0
  ) {
    next.templateEstCompletion = input.templateEstCompletion
  }
  rememberInstrumentCreateContext(next)
  return next
}

/** Persist Built-in Instrument choice from router / URL into session. */
export function syncCreateBuiltInIntoSession(input: {
  builtInInstrumentId: string
  templateName?: string
  templateEstCompletion?: number
}): InstrumentCreateSession | null {
  const ctx = readInstrumentCreateContext()
  if (!ctx) return null
  const builtInInstrumentId = input.builtInInstrumentId.trim()
  if (!builtInInstrumentId) return ctx
  const templateName =
    input.templateName?.trim() || ctx.templateName?.trim() || undefined
  const next: InstrumentCreateSession = {
    origin: ctx.origin === 'quick' ? 'quick' : 'campaign',
    campaignId: ctx.campaignId,
    loiId: ctx.loiId,
    builtInInstrumentId,
    ...(ctx.selectedTemplateId ? { selectedTemplateId: ctx.selectedTemplateId } : {}),
  }
  if (templateName) next.templateName = templateName
  if (
    typeof input.templateEstCompletion === 'number' &&
    input.templateEstCompletion >= 0
  ) {
    next.templateEstCompletion = input.templateEstCompletion
  }
  rememberInstrumentCreateContext(next)
  return next
}
